import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { format, isToday, isTomorrow } from 'date-fns';
import EventChip from './EventChip';
import type { CalendarEventInstance } from './types';

function dayHeading(date: Date): string {
  if (isToday(date)) return `Today · ${format(date, 'EEEE, MMMM d')}`;
  if (isTomorrow(date)) return `Tomorrow · ${format(date, 'EEEE, MMMM d')}`;
  return format(date, 'EEEE, MMMM d');
}

interface Props {
  eventsByDay: Map<string, CalendarEventInstance[]>;
  month: Date;
  onSelectEvent: (instance: CalendarEventInstance) => void;
  showGroup: boolean;
  /** Leader-only CTA on the empty state. */
  onNewEvent?: () => void;
}

/** Agenda view: the month's events grouped by day. Also the <lg fallback. */
export default function AgendaList({ eventsByDay, month, onSelectEvent, showGroup, onNewEvent }: Props) {
  const days = [...eventsByDay.keys()]
    .filter((key) => key.startsWith(format(month, 'yyyy-MM')))
    .sort();

  if (days.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-flock-gray-200 bg-white py-16 text-center shadow-sm">
        <CalendarDaysIcon className="h-10 w-10 text-flock-navy-200" />
        <p className="mt-3 text-sm font-medium text-flock-navy-600">
          No events in {format(month, 'MMMM')}
        </p>
        {onNewEvent && (
          <button
            onClick={onNewEvent}
            className="mt-3 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-flock-navy-700"
          >
            Plan something
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-6">
      {days.map((key) => {
        const instances = eventsByDay.get(key)!;
        const date = instances[0].occurrenceStart;
        return (
          <section key={key}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
              {dayHeading(date)}
            </h2>
            <div className="space-y-2">
              {instances.map((instance) => (
                <EventChip
                  key={instance.instanceKey}
                  instance={instance}
                  variant="row"
                  showGroup={showGroup}
                  onClick={() => onSelectEvent(instance)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
